import React, { useCallback, useEffect, useState } from "react";
import { Col, Container, Row, Spinner, Table } from "react-bootstrap";
import { CreateEditModal } from "./components/CreateEditModal";
import { CreateBillDto } from "./model/bill/dto/CreateBillDto";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { getInitialDto } from "./utils/getInitialDto";

import { useAppDispatch, useAppSelector } from "./redux/store";
import { billSliceSelectors } from "./redux/bill/bill.selector";
import { billActions } from "./redux/bill/bill.slice";
import { PageMeta } from "./model/meta/PageMeta";
import { GetLatestBillsDto } from "./model/bill/dto/GetLatestBillsDto";

import debounce from "lodash/debounce";

import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import { TableRowCells } from "./components/TableRow";
import { Bill } from "./model/bill/types/Bill";
import { DeleteModal } from "./components/DeleteModal";
import { UpdateBillDto } from "./model/bill/dto/UpdateBillDto";
import { pdf } from "@react-pdf/renderer";
import { PDFDocument } from "./table/PDFDocument";
import FileSaver from "file-saver";
import { BillDetails } from "./model/bill/types/BillDetails";
import { isEmpty } from "lodash";
import { FiltersModal } from "./components/FiltersModal";

export const Home: React.FC = () => {
  const [deleteBillModalVisible, setDeleteBillModalVisible] = useState(false);
  const [currentBill, setCurrentBill] = useState<Bill | null>(null);
  const [createEditBillModalVisible, setCreateEditBillModalVisible] =
    useState(false);
  const [filtersModalVisible, setFiltersModalVisible] = useState(false);

  const formMethods = useForm<CreateBillDto | UpdateBillDto>({
    defaultValues: getInitialDto(),
  });

  const { control, getValues, register, setValue } = useForm<GetLatestBillsDto>(
    {
      defaultValues: {
        keyword: "",
        creationDate: undefined,
        minTotalAmount: undefined,
        maxTotalAmount: undefined,
      },
    }
  );

  const onDelete = (bill: Bill) => {
    setCurrentBill(bill);
    setDeleteBillModalVisible(true);
  };

  const onUpdate = (bill: Bill) => {
    setCurrentBill(bill);
    setCreateEditBillModalVisible(true);
  };

  const onDownload = async (bill: Bill) => {
    const _bill = {
      ...bill,
      billDetails: Array.from(
        { length: 40 },
        (_, index) =>
          bill.billDetails?.find((b) => b.index === index) ||
          ({
            index,
            qty: "",
            code: "",
            description: "",
            unitPrice: "",
          } as BillDetails)
      ),
    };

    const blob = await pdf(<PDFDocument dto={_bill} />).toBlob();

    FileSaver.saveAs(blob, `shamautoparts-${bill.billNumber}.pdf`);
  };

  const onMail = async (bill: Bill) => {
    window.open(
      `mailto:?subject=Bill%20${bill.billNumber}&body=Please%20find%20the%20attached%20bill%20below.%0A%0AThank%20you,%0ASham%20Auto%20Parts.`,
      "_blank"
    );
  };

  const onCloseCreateEditModal = () => {
    if (currentBill) {
      setCurrentBill(null);
    }

    setCreateEditBillModalVisible(false);
  };

  const tableColumns = [
    "Edit",
    "Download",
    "Email",
    "Delete",
    "Bill Number",
    "Sold To",
    "Invoice Date",
    "Total Amount",
  ];

  const dispatch = useAppDispatch();

  const billsPageSize = useAppSelector(
    billSliceSelectors.selectBillsTablePageSize
  );
  const billsPage = useAppSelector(billSliceSelectors.selectBillsTablePage);
  const billsMeta = useAppSelector(billSliceSelectors.selectLatestBillsMeta);
  const billsLoading = useAppSelector(
    billSliceSelectors.selectLatestBillsLoading
  );
  const latestBills = useAppSelector(billSliceSelectors.selectLatestBills);
  const currentBillNumber = useAppSelector(
    billSliceSelectors.selectCurrentBillNumber
  );

  const onCloseBillModalVisible = () => {
    setCurrentBill(null);
    setDeleteBillModalVisible(false);
  };

  const onPageChange = async (page: number, pageSize: number) => {
    dispatch(billActions.setBillsTablePage(page));
  };

  const onFetchEntries = () => {
    const filter = omitBy(getValues(), isNil);
    const isFilterEmpty = Object.values(filter).every((v) => isEmpty(v));

    if (!isFilterEmpty) {
      dispatch(billActions.setBillsTablePage(1));
      dispatch(billActions.setBillsTablePageSize(10));
    }

    dispatch(
      billActions.getLatestBills({
        meta: !isFilterEmpty
          ? PageMeta.create({ count: 10, page: 0 })
          : {
              count: billsPageSize,
              page: billsPage - 1,
            },
        ...filter,
      })
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilter = useCallback(debounce(onFetchEntries, 700), [
    onFetchEntries,
  ]);

  useEffect(() => {
    if (currentBillNumber > 0) {
      formMethods.reset(getInitialDto());
    }
  }, [currentBillNumber, formMethods]);

  useEffect(() => {
    dispatch(
      billActions.getLatestBills({
        ...omitBy(getValues(), isNil),
        meta: PageMeta.create({
          count: billsPageSize,
          page: billsPage - 1,
        }),
      })
    );
  }, [dispatch, billsPage, billsPageSize, formMethods, getValues]);

  useEffect(() => {
    dispatch(billActions.getBillNumber());
  }, [dispatch]);

  return (
    <FormProvider {...formMethods}>
      <section className="mt-5">
        <Container fluid>
          <Row className="gap-2">
            <Col xs={12}>
              <div className="d-flex flex-column gap-2 w-100">
                <div className="d-flex align-items-center justify-content-between flex-grow-1 gap-2">
                  <Controller
                    control={control}
                    name="keyword"
                    render={({ field: { value, onChange } }) => (
                      <input
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                          debouncedFilter();
                        }}
                        className="form-control filter-input"
                        id="keyword"
                        placeholder="Bill Number, received by or sold to"
                      />
                    )}
                  />

                  <div className="d-flex gap-2 align-items-center me-auto">
                    <button
                      onClick={() => setFiltersModalVisible(true)}
                      className="px-md-3 bg-transparent border-0 d-none d-sm-block d-lg-none"
                    >
                      <i className="bi bi-funnel-fill text-secondary fs-4"></i>
                    </button>

                    <button
                      className="create-bill-btn btn btn-primary py-1 px-md-3 rounded-2 d-flex align-items-center justify-content-center"
                      onClick={() => setCreateEditBillModalVisible(true)}
                    >
                      <span className="d-none d-sm-inline">Create</span>
                      <i className="bi bi-plus text-white fs-4"></i>
                    </button>
                  </div>
                </div>

                <div className="d-none d-lg-flex align-items-center gap-2 filters-wrapper overflow-x-auto pb-2">
                  <Controller
                    control={control}
                    name="minTotalAmount"
                    render={({ field: { value, onChange } }) => (
                      <input
                        type="text"
                        className="form-control filter-input"
                        placeholder="Min Total Amount"
                        value={value}
                        onChange={(e) => {
                          const numericValue = e.currentTarget.value.replace(
                            /[^0-9.]/g,
                            ""
                          );
                          onChange(numericValue);
                          debouncedFilter();
                        }}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="maxTotalAmount"
                    render={({ field: { value, onChange } }) => (
                      <input
                        type="text"
                        className="form-control filter-input"
                        placeholder="Max Total amount"
                        value={value}
                        onChange={(e) => {
                          const numericValue = e.currentTarget.value.replace(
                            /[^0-9.]/g,
                            ""
                          );
                          onChange(numericValue);
                          debouncedFilter();
                        }}
                      />
                    )}
                  />

                  <input
                    className="form-control filter-input"
                    placeholder="Date"
                    type="date"
                    {...register("creationDate", {
                      onChange: (e) => {
                        debouncedFilter();
                        setValue("creationDate", e.target.value);
                      },
                    })}
                  />
                </div>

                <button
                  onClick={() => setFiltersModalVisible(true)}
                  className="ps-3 pe-3 bg-transparent border-0 d-sm-none me-auto"
                >
                  <i className="bi bi-funnel-fill text-secondary fs-4"></i>
                </button>
              </div>
            </Col>

            <Col xs={12} className="bills-table-column">
              <Table
                striped
                bordered
                hover
                size="sm"
                responsive
                className={`text-center ${billsLoading ? "opacity-50" : ""}`}
              >
                <thead className="position-sticky top-0 shadow-sm">
                  <tr>
                    {tableColumns.map((col) => (
                      <th
                        key={col}
                        className={`text-nowrap ${col
                          .replaceAll(" ", "-")
                          .toLowerCase()}-column`}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {billsLoading && !latestBills?.length ? (
                    <tr>
                      <td colSpan={5}>
                        <Spinner animation="border" />
                      </td>
                    </tr>
                  ) : !latestBills?.length ? (
                    <tr>
                      <td colSpan={5}>
                        <span>No bills</span>
                      </td>
                    </tr>
                  ) : (
                    latestBills.map((bill) => (
                      <TableRowCells
                        key={bill._id}
                        data={bill}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                        onDownload={onDownload}
                        onSaveEmail={onMail}
                      />
                    ))
                  )}
                </tbody>
              </Table>
            </Col>

            {billsMeta?.hasNext || billsPage >= 1 ? (
              <Col xs={12}>
                <div className="d-flex gap-2 justify-content-end">
                  <button
                    className="btn btn-outline-primary bg-white"
                    onClick={() => onPageChange(billsPage - 1, billsPageSize)}
                    disabled={billsLoading || billsPage <= 1}
                  >
                    {"<"}
                  </button>
                  <span className="align-self-center mx-2">
                    Page {billsPage} of{" "}
                    {Math.ceil((billsMeta?.total || 0) / billsPageSize)}
                  </span>
                  <button
                    className="btn btn-outline-primary bg-white"
                    onClick={() => onPageChange(billsPage + 1, billsPageSize)}
                    disabled={billsLoading || !billsMeta?.hasNext}
                  >
                    {">"}
                  </button>
                </div>
              </Col>
            ) : null}
          </Row>
        </Container>
      </section>

      <CreateEditModal
        open={createEditBillModalVisible}
        onClose={onCloseCreateEditModal}
        bill={currentBill}
      />

      <DeleteModal
        open={deleteBillModalVisible}
        onClose={onCloseBillModalVisible}
        bill={currentBill}
      />

      <FiltersModal
        open={filtersModalVisible}
        onClose={() => setFiltersModalVisible(false)}
      />
    </FormProvider>
  );
};
